import { TFormField } from '@nirmata/shared';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nirmata/web-components/ui/tabs';
import { X } from 'lucide-react';
import { useCallback } from 'react';

import { useBuilderStore } from '@/store/builder';

import { BasicProperties } from './sections/BasicProperties';
import { ConditionalRulesEditor } from './sections/ConditionalRulesEditor';
import { ValidationsEditor } from './sections/ValidationsEditor';

interface PropertyEditorProps {
  field: TFormField;
  updateField: (
    fieldId: string,
    key: string,
    value: string | Record<string, unknown> | unknown[]
  ) => void;
}

export function PropertyEditor({ field, updateField }: PropertyEditorProps) {
  const setSelectedField = useBuilderStore((state) => state.setSelectedField);

  const handleFieldUpdate = useCallback(
    (updates: Partial<TFormField>) => {
      if (updates.fieldName && updates.fieldName !== field.fieldName) {
        updateField(field.fieldId, 'fieldName', updates.fieldName);
      }
      if (updates.componentProps) {
        updateField(field.fieldId, 'componentProps', updates.componentProps);
      }
      if (updates.validationRules) {
        updateField(field.fieldId, 'validationRules', updates.validationRules);
      }
      if (updates.conditionalRules) {
        updateField(field.fieldId, 'conditionalRules', updates.conditionalRules);
      }
    },
    [field, updateField]
  );

  return (
    <div className="relative w-full h-full flex flex-col">
      <button
        className="absolute top-2 right-2 p-1 rounded hover:bg-[var(--muted)] cursor-pointer"
        onClick={() => setSelectedField(null)}
        title="Close"
        type="button"
      >
        <X className="w-5 h-5 text-[var(--muted-foreground)]" />
      </button>
      <Tabs defaultValue="basic" className="w-full h-full flex flex-col">
        <TabsList className="mb-2">
          <TabsTrigger value="basic">Basic Properties</TabsTrigger>
          <TabsTrigger value="validation">Validation Rules</TabsTrigger>
          <TabsTrigger value="conditional">Conditional Logic</TabsTrigger>
        </TabsList>
        <TabsContent value="basic">
          <BasicProperties
            field={field}
            onChange={(key: string, value: string | Record<string, unknown>) =>
              handleFieldUpdate({ [key]: value })
            }
          />
        </TabsContent>
        <TabsContent value="validation">
          <ValidationsEditor
            field={field}
            onChange={(updates) => handleFieldUpdate({ validationRules: updates.validationRules })}
          />
        </TabsContent>
        <TabsContent value="conditional">
          <ConditionalRulesEditor
            field={field}
            onChange={(updates) =>
              handleFieldUpdate({ conditionalRules: updates.conditionalRules })
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
